"use client";
import { Form, FormProps } from "@/components/Form/Form";
import { prepopulateFormFields } from "@/helpers";
import {
  decryptString,
  getRegistrationDetails,
} from "@/libs/builder/builderApi";
import { BuilderBlockProps } from "@/types";
import { BuilderProps } from "@/types/builder/BuilderProps";
import { useRouter } from "next/navigation";
import {
  FC,
  SubmitEvent,
  Suspense,
  useEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import { BuilderFormsContextProvider } from "@/contexts";

const subscribe = () => () => {};
const getSnapshot = () => true;
const getServerSnapshot = () => false;

type FormBlockProps = Omit<FormProps, "onSubmit"> &
  BuilderBlockProps &
  BuilderProps & {
    onSubmit?: (
      event: SubmitEvent<HTMLFormElement>,
      router: unknown,
      correlationId: string,
    ) => Promise<unknown>;
  };

export const FormBlock: FC<FormBlockProps> = ({
  attributes,
  builderBlock,
  builderComponents,
  builderLinkComponent,
  builderContext,
  onSubmit,
  ...props
}) => {
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);
  const prepopulatedRef = useRef(false);

  const hydrated = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  useEffect(() => {
    if (!hydrated || prepopulatedRef.current) return;
    prepopulatedRef.current = true;

    (async () => {
      try {
        const registrationDetails = await getRegistrationDetails();
        if (!registrationDetails) return;

        const registrationData = await decryptString(registrationDetails);
        if (!registrationData || !formRef.current) return;

        prepopulateFormFields(formRef.current, registrationData);
      } catch (error) {
        console.error("Failed to prepopulate form from cookie", error);
      }
    })();
  }, [hydrated]);

  const onFormSubmit = async (
    event: SubmitEvent<HTMLFormElement>,
    correlationId: string,
  ) => {
    await onSubmit?.(event, router, correlationId);
  };

  return (
    <BuilderFormsContextProvider>
      <Form
        {...attributes}
        noValidate
        {...props}
        ref={formRef}
        inert={!hydrated}
        onSubmit={onFormSubmit}
      />
    </BuilderFormsContextProvider>
  );
};
